import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bot, Plus, Trash2, Mic, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, Empty, StatusPill, Toggle } from "@/components/bits";

export default function Agents() {
  const [agents, setAgents] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const load = () => api.get("/agents").then((r) => setAgents(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { await api.delete(`/agents/${id}`); toast.success("Agent deleted"); load(); };

  const toggleActive = async (a) => {
    const next = a.active === false;
    setBusyId(a.id);
    try {
      await api.put(`/agents/${a.id}`, { active: next });
      toast.success(next ? "Agent activated" : "Agent paused");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update the agent");
    } finally { setBusyId(null); }
  };

  const duplicate = async (a) => {
    setBusyId(a.id);
    try {
      const { data } = await api.post(`/agents/${a.id}/duplicate`);
      toast.success(`Duplicated as “${data.name}” (draft)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplicate failed");
    } finally { setBusyId(null); }
  };

  return (
    <div>
      <PageHeader overline="Agents" title="Voice & chat agents" desc="Template-based per state, customised on prompt, voice, model, language, knowledge base and tools." testId="agents-header"
        action={<Link to="/dashboard/agents/new" className="btn-primary" data-testid="agents-new-btn"><Plus className="w-4 h-4" /> New agent</Link>} />
      {agents && agents.length === 0 && <Empty title="No agents yet" desc="Pick a state-wise template for farmers or retailers, tune it, and test it live before publishing." cta="Build your first agent" to="/dashboard/agents/new" testId="agents-empty" />}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {agents?.map((a) => (
          <div key={a.id} className={`glass p-5 rise flex flex-col ${a.active === false ? "opacity-70" : ""}`} data-testid={`agent-card-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${a.audience === "farmer" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}><Bot className="w-5 h-5" /></div>
              <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1.5 min-w-0">
                <StatusPill status={a.status} />
                <span className="flex items-center gap-2 shrink-0 rounded-full glass-soft pl-2.5 pr-1.5 py-1" title={a.active === false ? "Inactive — paused for campaigns" : "Active — campaigns can use this agent"}>
                  <span className="text-[10px] font-mono uppercase tracking-wide text-slate-500">{a.active === false ? "paused" : "active"}</span>
                  <Toggle on={a.active !== false} onChange={() => toggleActive(a)} testId={`agent-active-${a.id}`} />
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-4">
              <h3 className="font-display font-semibold text-slate-100">{a.name}</h3>
              {a.version ? <span className="pill-muted" data-testid={`agent-version-${a.id}`}>v{a.version}</span> : null}
            </div>
            <p className="text-xs text-slate-500 mt-1">{a.state} · {a.language} ({a.dialect})</p>
            <div className="flex flex-wrap gap-1.5 mt-3"><span className={a.audience === "farmer" ? "pill" : "pill-gold"}>{a.audience}</span><span className="pill-muted flex items-center gap-1"><Mic className="w-3 h-3" />{a.voice}</span><span className="pill-muted">{a.model}</span></div>
            <p className="text-xs text-slate-500 mt-3">{a.tools?.length || 0} tools · {a.kb_sources?.length || 0} KB sources · {a.calls || 0} calls{a.channels?.length ? ` · ${a.channels.length} channels` : ""}</p>
            <div className="flex items-center gap-2 mt-5 pt-4 border-t border-white/5">
              <Link to={`/dashboard/agents/${a.id}`} className="btn-ghost btn-sm flex-1" data-testid={`agent-edit-${a.id}`}>Open & test</Link>
              <button onClick={() => duplicate(a)} disabled={busyId === a.id} className="btn-ghost btn-sm" data-testid={`agent-duplicate-${a.id}`} title="Duplicate as a new draft"><Copy className="w-3.5 h-3.5" /> Duplicate</button>
              <button onClick={() => del(a.id)} className="text-slate-500 hover:text-rose-300 p-2" data-testid={`agent-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
